// Offline session lifecycle: the boot snapshot that lets a cold offline load
// reach the dashboard, the inactivity LOCK that replaces logout while offline
// mode is enabled, and the logout confirmation that protects unsynced work.
//
// Every export is a strict no-op / passthrough when offline mode is disabled,
// so orgs without the flag see byte-for-byte existing behavior.

import offlineDb from './db';
import { isOfflineModeEnabled, setOfflineModeUser } from './featureFlag';
import { connectivityMonitor } from './connectivityMonitor';
import { syncQueueService } from './syncQueue';
import { getOriginalRequest } from './offlineMiddleware';
import { useOfflineSessionStore } from './offlineSessionStore';
import { authService, isNetworkError } from '../auth';

const TRUST_WINDOW_MS = 24 * 60 * 60 * 1000; // matches the Xano token's hard expiry
const BOOT_TIMEOUT_MS = 15000;
const LOCK_FLAG_KEY = 'prism_offline_locked';
const LAST_ACTIVITY_KEY = 'erx_last_activity';
const DRAIN_WAIT_MS = 30000;

function tokenTail(token) {
  return typeof token === 'string' ? token.slice(-12) : null;
}

// ---------------------------------------------------------------------------
// Snapshot (WS-A)
// ---------------------------------------------------------------------------

// Called with the freshly loaded /auth/me profile on every login/refresh
// (replaces the bare setOfflineModeUser call in App.jsx).
export async function rememberUser(user) {
  setOfflineModeUser(user);
  if (!user?.id || !user?.organization?.id) return;

  try {
    if (isOfflineModeEnabled()) {
      await offlineDb.offlineSession.put({
        key: 'profile',
        userId: user.id,
        organizationId: user.organization.id,
        tokenTail: tokenTail(authService.getToken()),
        tokenMintedAt: parseInt(localStorage.getItem('xano_token_minted_at'), 10) || Date.now(),
        savedAt: Date.now(),
        data: user,
      });
    } else {
      await offlineDb.offlineSession.bulkDelete(['profile', 'solutions', 'orgRole']);
    }
  } catch (e) {
    console.warn('[OfflineSession] Failed to persist session snapshot:', e.message);
  }
}

// Wraps the boot-time token validation with a timeout so a HUNG /auth/me
// (no fetch timeout exists in authService) cannot spin the boot forever.
// Passthrough when offline mode is disabled.
export function withBootTimeout(factory, ms = BOOT_TIMEOUT_MS) {
  if (!isOfflineModeEnabled()) return factory();
  return Promise.race([
    factory(),
    new Promise((resolve) =>
      setTimeout(() => resolve({ valid: false, reason: 'timeout', retryable: true }), ms)
    ),
  ]);
}

// Attempt to hydrate the session from the snapshot after a retryable boot
// failure. Returns { profile } on success; otherwise sets bootState
// 'blocked' (the reconnect wall renders) and returns null.
export async function restoreOfflineSession() {
  const store = useOfflineSessionStore.getState();
  const blocked = (reason) => {
    store.setBootState('blocked', reason);
    return null;
  };

  try {
    const profileRow = await offlineDb.offlineSession.get('profile');
    if (!profileRow?.data) return blocked('no-snapshot');

    const currentTail = tokenTail(authService.getToken());
    if (!currentTail || profileRow.tokenTail !== currentTail) {
      return blocked('snapshot-mismatch');
    }

    const age = authService.getTokenAgeMs();
    if (age === null || age >= TRUST_WINDOW_MS) {
      return blocked('token-expired');
    }

    const solutionsRow = await offlineDb.offlineSession.get('solutions');
    if (!solutionsRow || solutionsRow.userId !== profileRow.userId || solutionsRow.organizationId !== profileRow.organizationId) {
      return blocked('no-snapshot');
    }

    const profile = profileRow.data;
    if (profile.org_role_id && profile.role !== 'Admin') {
      const roleRow = await offlineDb.offlineSession.get('orgRole');
      if (!roleRow || String(roleRow.roleId) !== String(profile.org_role_id)) {
        return blocked('no-snapshot');
      }
    }

    setOfflineModeUser(profile);
    store.setBootState('hydrated');
    return { profile };
  } catch (e) {
    console.error('[OfflineSession] restore failed:', e);
    return blocked('no-snapshot');
  }
}

// Belt-and-braces for the FeatureContext catch: a profile snapshot may exist
// while the solutions row is missing, which would otherwise dead-end on the
// dashboard spinner.
export function reportBootFailure(stage, error) {
  if (!isOfflineModeEnabled()) return;
  if (connectivityMonitor.isOnline() && !isNetworkError(error) && error?.offline !== true) return;
  useOfflineSessionStore.getState().setBootState('blocked', `${stage}-unavailable`);
}

// ---------------------------------------------------------------------------
// Lock instead of logout (WS-B)
// ---------------------------------------------------------------------------

// Replaces the inactivity teardown when offline mode is enabled. Returns
// true when it handled the event (caller must NOT log out / purge); false
// when disabled (caller runs the existing teardown).
export function lockIfOfflineMode(reason = 'inactivity') {
  if (!isOfflineModeEnabled()) return false;
  useOfflineSessionStore.getState().lock(reason);
  try {
    localStorage.setItem(LOCK_FLAG_KEY, '1');
  } catch { /* ignore */ }
  return true;
}

export function isLockPersisted() {
  try {
    return localStorage.getItem(LOCK_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

export function stampActivity() {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  } catch { /* ignore */ }
}

function clearLockFlag() {
  try {
    localStorage.removeItem(LOCK_FLAG_KEY);
  } catch { /* ignore */ }
}

// Unlock policy: online, re-prove the session (silent Auth0 + Xano
// re-exchange, which also resets the 24h trust window; a transiently failing
// Xano falls back to an /auth/me liveness check). Offline, "Continue" is
// allowed within the 24h trust window — the OS screen lock is the stated
// access control for this round; past the window the lock hardens until
// connectivity returns.
export async function unlockSession() {
  const store = useOfflineSessionStore.getState();

  if (connectivityMonitor.isOnline()) {
    try {
      await authService.refreshXanoToken();
      stampActivity();
      clearLockFlag();
      store.unlock();
      return { ok: true };
    } catch (error) {
      if (!isNetworkError(error)) {
        try {
          await authService.getUserProfile();
          stampActivity();
          clearLockFlag();
          store.unlock();
          return { ok: true };
        } catch (profileError) {
          if (profileError.status === 401 || profileError.status === 403) {
            // Terminal: existing auth:session-expired / reAuthenticate paths
            // take over once online interaction resumes.
            return { ok: false, terminal: true };
          }
        }
      }
      // Network-class failure: fall through to the offline rule.
    }
  }

  const age = authService.getTokenAgeMs();
  if (age !== null && age < TRUST_WINDOW_MS) {
    stampActivity();
    clearLockFlag();
    store.unlock();
    return { ok: true };
  }

  store.setLockReason('trust-window-expired');
  return { ok: false, terminal: false };
}

// ---------------------------------------------------------------------------
// Logout protection (WS-B)
// ---------------------------------------------------------------------------

async function waitForQueueIdle() {
  if (!syncQueueService.isProcessing) return;
  await new Promise((resolve) => {
    const timer = setTimeout(done, DRAIN_WAIT_MS);
    const unsubscribe = syncQueueService.subscribe((event) => {
      if (event === 'sync:complete' || event === 'sync:error') done();
    });
    function done() {
      clearTimeout(timer);
      unsubscribe();
      resolve();
    }
  });
}

// Returns true when logout may proceed (queue empty, drained, or the user
// explicitly chose to discard); false when the user cancelled. Always true
// when offline mode is disabled. Must run BEFORE authService.clearToken().
export async function confirmLogout() {
  if (!isOfflineModeEnabled()) return true;

  try {
    if (connectivityMonitor.isOnline() || connectivityMonitor.isRecovering()) {
      const originalRequest = getOriginalRequest();
      if (originalRequest) {
        await waitForQueueIdle();
        await syncQueueService.processQueue(originalRequest);
        await waitForQueueIdle();
      }
    }

    const [pending, failed] = await Promise.all([
      syncQueueService.getPendingCount(),
      syncQueueService.getFailedCount(),
    ]);
    const count = pending + failed;
    if (count === 0) return true;

    // Hand the decision to the user via the confirmation dialog.
    return await new Promise((resolve) => {
      useOfflineSessionStore.getState().setLogoutPrompt({
        count,
        resolve: (discard) => {
          useOfflineSessionStore.getState().setLogoutPrompt(null);
          resolve(discard);
        },
      });
    });
  } catch (e) {
    console.error('[OfflineSession] confirmLogout check failed:', e);
    // Fail open: never trap the user in a session they asked to leave.
    return true;
  }
}
